const img = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1200&q=80`;

export const guides = [
  {
    slug: 'tic-tac-toe-strategy',
    title: 'Tic-Tac-Toe Strategy: How to Never Lose a Game',
    description:
      'The opening moves, forks and blocks that turn Tic-Tac-Toe from a coin flip into a game you never lose — explained with simple square-by-square examples.',
    category: 'Game guide',
    readMinutes: 6,
    published: '2026-11-03',
    image: img('photo-1668901382969-8c73e450a1f5'),
    imageAlt: 'A wooden Tic-Tac-Toe board with X and O pieces',
    sections: [
      {
        heading: 'Why Tic-Tac-Toe is a “solved” game',
        body: [
          'Tic-Tac-Toe has only 255,168 possible games, small enough that computers mapped every one of them decades ago. The result: if both players make no mistakes, every game ends in a draw. That sounds boring, but it is actually great news — it means that if you learn a handful of patterns you will never lose, and you will win every time your opponent slips.',
          'On DashLink, X always moves first and the server checks every move, so you can focus entirely on strategy. To follow this guide, number the squares like a phone keypad: 1-2-3 across the top, 4-5-6 in the middle and 7-8-9 at the bottom. Square 5 is the centre, 1, 3, 7 and 9 are corners, and 2, 4, 6 and 8 are edges.',
        ],
      },
      {
        heading: 'The three priorities on every turn',
        body: [
          'Before every move, run through this checklist in order. It sounds simple, but most lost games come from skipping step two.',
          {
            list: [
              'Win: if you have two in a line and the third square is empty, take it.',
              'Block: if your opponent has two in a line with the third square empty, take that square — even if you had a clever plan.',
              'Build: otherwise, make a move that creates a fork (two winning threats at once) or prevents your opponent from making one.',
            ],
          },
        ],
      },
      {
        heading: 'Playing first as X',
        body: [
          'The strongest practical first move is a corner. The centre is also perfectly safe, but a corner gives your opponent the most ways to go wrong. Of the seven replies O can make to a corner opening, only one — taking the centre — avoids a forced loss.',
          'Example: you play 1. If O answers with an edge such as 2, play 5 (the centre). Now you threaten 9 to complete the 1-5-9 diagonal, so O must block at 9. Take 7: you now threaten both 4 (the 1-4-7 column) and 3 (the 3-5-7 diagonal). O can only block one of them, and you win on the next move. That double threat is called a fork, and it is the only way to beat a careful opponent.',
          'If O correctly takes the centre after your corner, take the opposite corner (9). Many players now panic and play a corner, which lets you fork. If they play an edge, simply keep blocking and the game will be drawn.',
        ],
      },
      {
        heading: 'Playing second as O',
        body: [
          'As O your goal is first not to lose. Use this rule: if X opens in a corner, take the centre. If X opens in the centre, take a corner. If X opens on an edge, take the centre or the corner next to their mark.',
          'The most common trap for O is the “opposite corners” position: X has 1 and 9, O has 5. Your instinct says to take another corner, but that hands X a fork. The correct move is an edge (2, 4, 6 or 8), which forces X to block you and kills every fork. Remember: when X holds two opposite corners, O plays an edge.',
        ],
      },
      {
        heading: 'Spotting forks before they happen',
        body: [
          'A fork almost always uses a corner that sits on two lines your opponent already partly controls. Before you move, look at every empty corner and ask: “If my opponent took this square, would they threaten two lines at once?” If the answer is yes, either take that square yourself or make a move that forces them to respond somewhere else.',
          'Forcing moves are your best defensive tool. If you create a two-in-a-row, your opponent must block it, and you get to choose where their mark goes. Good players use forcing moves to steer the opponent’s marks into squares where they cannot fork.',
        ],
      },
      {
        heading: 'Practising on DashLink',
        body: [
          'Open Tic-Tac-Toe from the games list, create a room and share the room code with a friend, or use “Find Players” to see who else is looking for a match. Wins add to your score on the global and per-game leaderboards, so every fork you land counts. Try playing ten games as X using only corner openings, then ten as O using the edge-defence rule — you will feel the difference immediately.',
        ],
      },
    ],
  },
  {
    slug: 'checkers-rules-and-strategy',
    title: 'Checkers Rules and Beginner Strategy',
    description:
      'Everything you need to start playing Checkers online: board setup, moves, mandatory captures, multi-jumps and kings — plus five strategy tips that win games.',
    category: 'Game guide',
    readMinutes: 8,
    published: '2026-11-03',
    image: img('photo-1610232826230-e5e6c6a1efef'),
    imageAlt: 'Red and black checkers pieces on a checkered board',
    sections: [
      {
        heading: 'The board and setup',
        body: [
          'Checkers (called Draughts in the UK and many Commonwealth countries) is played on an 8×8 board using only the 32 dark squares. Each player starts with 12 pieces on the three rows closest to them. On DashLink, Red starts at the bottom of the board and Black at the top, and — following American Checkers rules — Red always moves first.',
        ],
      },
      {
        heading: 'How pieces move',
        body: [
          {
            list: [
              'A normal piece (a “man”) moves one square diagonally forward onto an empty dark square.',
              'To capture, jump diagonally over an opponent’s piece that is directly next to you, landing on the empty square right behind it. The jumped piece is removed.',
              'Captures are mandatory. If any of your pieces can jump, you must make a jump that turn — DashLink’s server will reject a normal move while a capture is available.',
              'Multi-jumps: if your piece lands somewhere it can jump again, it must keep jumping in the same turn. The game will lock you to that piece until the chain is finished.',
              'Kings: when a piece reaches the far row it is crowned a king. Kings can move and capture diagonally both forwards and backwards.',
            ],
          },
        ],
      },
      {
        heading: 'How to win',
        body: [
          'You win by capturing all of your opponent’s pieces, or by leaving them with no legal move on their turn — a fully blocked opponent loses even if they still have pieces on the board. Because blocking counts as a win, controlling space is just as important as capturing.',
        ],
      },
      {
        heading: 'Five strategy tips for beginners',
        body: [
          { sub: '1. Control the centre' },
          'Pieces in the middle of the board have two forward options and support each other. Pieces on the edge only have one direction to move and are easy to trap. Aim to move your pieces toward the four central squares early.',
          { sub: '2. Keep your back row as long as possible' },
          'Your opponent needs to reach your back row to make a king. Leaving two or three back-row pieces in place for most of the game makes it very hard for them to crown. Move them only when you need to or when the endgame begins.',
          { sub: '3. Move in pairs' },
          'A single piece that runs ahead can be captured for free. Advance pieces so they sit diagonally behind one another; a jumped piece is then immediately recaptured, which discourages your opponent from attacking.',
          { sub: '4. Use forced captures against your opponent' },
          'Because jumps are compulsory, you can “give away” one piece to force your opponent to jump into a square where you then capture two or three of theirs. Before every move, check what your opponent will be forced to do next — this single habit wins more games than anything else.',
          { sub: '5. Trade when you are ahead' },
          'When you have more pieces, even trades (one for one) make your advantage bigger in proportion. When you are behind, avoid trades and look for blocking positions instead.',
        ],
      },
      {
        heading: 'Common beginner mistakes',
        body: [
          {
            list: [
              'Forgetting that captures are mandatory and walking into a double jump.',
              'Moving every piece forward at once, leaving gaps that let enemy pieces slip through to be crowned.',
              'Chasing a single capture and ignoring the multi-jump your opponent gets in return.',
              'Keeping kings in a corner where they can be trapped instead of using them actively.',
            ],
          },
        ],
      },
      {
        heading: 'Play Checkers on DashLink',
        body: [
          'Choose Checkers in the games list, create a room and send the code to a friend, or use “Find Players” to match with someone who is looking for a game right now. You can chat in the side panel while you play, and wins count toward the Checkers leaderboard.',
        ],
      },
    ],
  },
  {
    slug: 'how-to-find-a-gaming-squad',
    title: 'How to Find a Gaming Squad Online (and Keep It)',
    description:
      'Practical advice for finding teammates who match your skill, schedule and vibe — and the habits that turn random matches into a regular squad.',
    category: 'Community',
    readMinutes: 6,
    published: '2026-11-03',
    image: img('photo-1659535915214-e7cbac112038'),
    imageAlt: 'Two friends with headsets playing video games together',
    sections: [
      {
        heading: 'Why a regular squad matters',
        body: [
          'Playing with the same people regularly is more fun and usually more successful than queuing with strangers. You learn each other’s habits, communication gets shorter and clearer, and losses sting less when you can laugh about them together. The hard part is the first step: finding people worth playing with.',
        ],
      },
      {
        heading: 'Decide what you are looking for',
        body: [
          'Before you send a single invite, be clear about three things:',
          {
            list: [
              'Game and mode — a ranked team needs different people than a casual weekend group.',
              'Schedule and time zone — the friendliest teammate is no help if they are asleep when you play.',
              'Tone — competitive and focused, or relaxed and chatty? Neither is wrong, but mixing them causes friction.',
            ],
          },
        ],
      },
      {
        heading: 'Using “Looking for Squad” on DashLink',
        body: [
          'Open any game from the games list and press “Find Players”. This adds you to that game’s Looking for Squad list for about 30 minutes and shows everyone else who is searching right now. Send an invite to anyone on the list; when they accept, you are both taken straight into a shared room with its own chat.',
          'Treat the first game as a trial. Say hello in the room chat, mention your usual play times, and keep it light. If it clicks, tap the heart to save them to your Favourites — next time they are one tap away, even if they are not on the LFS list.',
        ],
      },
      {
        heading: 'Be the teammate people want back',
        body: [
          {
            list: [
              'Communicate early and briefly: callouts, plans and “nice one” go a long way.',
              'Blame the situation, not the person. Rewatching a mistake together beats arguing about it.',
              'Show up when you say you will, and say so if you cannot.',
              'Respect boundaries — not everyone wants to share personal details or move to another platform.',
            ],
          },
        ],
      },
      {
        heading: 'Stay safe while meeting new people',
        body: [
          'Keep conversations on DashLink until you trust someone. Never share passwords, your address or payment details, and be wary of anyone offering free in-game currency or asking for gifts. If someone makes you uncomfortable, stop replying and report them — see our Community Guidelines for how.',
        ],
      },
    ],
  },
  {
    slug: 'hosting-a-watch-party',
    title: 'Hosting a Watch Party: Streams, Chat and Etiquette',
    description:
      'How to watch Twitch and YouTube streams together with your squad on DashLink, keep the chat fun, and avoid the common watch-party pitfalls.',
    category: 'Community',
    readMinutes: 5,
    published: '2026-11-03',
    image: img('photo-1542751371-adc38448a05e'),
    imageAlt: 'A player watching an esports stream on a gaming setup',
    sections: [
      {
        heading: 'What a watch party is',
        body: [
          'A watch party lets everyone in a DashLink room watch the same live stream or video while chatting alongside it. It is a great way to follow a tournament together, study a pro player’s game, or just hang out with your squad between matches.',
        ],
      },
      {
        heading: 'Starting a watch party',
        body: [
          {
            list: [
              'Create or join a room from any game.',
              'Paste a Twitch channel link (for example twitch.tv/channelname) or a YouTube video link into the Watch Party box above the chat.',
              'The stream appears for everyone in the room instantly. Anyone in the room can replace it with a new link or clear it.',
            ],
          },
          'The video is played directly from Twitch or YouTube, so each viewer’s own connection and the platform’s availability apply. If a stream will not play, check that the channel is live and that the video allows embedding.',
        ],
      },
      {
        heading: 'Watch-party etiquette',
        body: [
          {
            list: [
              'Agree on what to watch before switching streams — changing the video for everyone mid-match is the fastest way to annoy a room.',
              'Avoid spoilers for people who are behind on a tournament or story.',
              'Only share official streams and videos; pirated broadcasts break our Community Guidelines and the platforms’ terms.',
              'Use emojis and short reactions during big moments, and save long discussions for breaks.',
            ],
          },
        ],
      },
      {
        heading: 'Make it a regular thing',
        body: [
          'Big esports weekends are perfect for a recurring watch party. Save the people you enjoy watching with as Favourites, send them squad invites a few minutes before the broadcast, and keep the room chat as your shared second screen.',
        ],
      },
    ],
  },
];

export const getGuide = (slug) => guides.find((g) => g.slug === slug);
